import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as _ from "lodash";
import Editor from "../../components/Editing/Editor";
import TextField from "../../components/Editing/TextField";
import { nameofFactory, hasWriteAccess } from "../../utils/utils";
import { liftProductHasChildren } from "../../utils/pdApiUtils";
import NumberField from "../../components/Editing/NumberField";
import { EditActions, editActions } from "../../actions/editActions";


type StateProps = {
  editedProduct: Api.KceProduct;
  storedProduct: Api.KceProduct;
  pdApi: State.PdApi;
  activeUser: Api.User | undefined;
  ioConfigurations: Api.IoConfiguration[] | undefined;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  toggleModal: () => void;
};

const kceProductFields = nameofFactory<Api.KceProduct>();

export type EditKCEProductProps = StateProps & DispatchProps & OwnProps;

export class EditKCEProduct extends React.Component<EditKCEProductProps> {
  render(): JSX.Element {
    const { editedProduct, storedProduct, activeUser, toggleModal, pdApi, ioConfigurations } = this.props;
    const isNew = !storedProduct;
    const { liftParameters } = pdApi;
    const deleteDisabled =  storedProduct && liftProductHasChildren(storedProduct.productNumber, liftParameters, ioConfigurations || []);
    
    return (
      <Editor
        itemName={"KCE product"}
        originalObject={storedProduct}
        editedObject={editedProduct}
        hasWritePermission={hasWriteAccess(activeUser, "Lift")}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => this.props.editActions.update("kceProducts")}
        create={() => this.props.editActions.add("kceProducts")}
        remove={{ callback: toggleModal
          , enabledTooltip: "product"
          , disabled: deleteDisabled
          , disabledTooltip: ": Item linked to child components" }}
      >
        <NumberField
          id="code"
          label="Code"
          value={editedProduct.productNumber}
          isError={!this.isFieldValid(kceProductFields("productNumber"))}
          errorMessage="The code must be unique."
          readonly={!isNew}
          onChange={productNumber =>
            this.props.editActions.setEdited({ ...editedProduct, productNumber })
          }
        />
        <TextField
          id="name"
          label="Name"
          value={editedProduct.productName}
          isError={!this.isFieldValid(kceProductFields("productName"))}
          errorMessage={!this.isEmptyString(editedProduct.productName) ? "The name must be unique." : ""}
          readonly={!isNew}
          onChange={productName =>this.props.editActions.setEdited({ ...editedProduct, productName: productName.trim() })   
          }
        />
        <TextField
          id="comment"
          label="Comment"
          value={editedProduct.comment}
          onChange={comment => this.props.editActions.setEdited({ ...editedProduct, comment: comment.trim() })}
          readonly={!hasWriteAccess(activeUser, "Lift")}
        />
      </Editor>
    );
  }
 
  private isEmptyString = (str: string): boolean => !(!!str && str.replace(/\s/g, "").length > 0);  
 
  
  private isFieldValid = (field: string): boolean => {
    const { editedProduct, pdApi } = this.props;    
    switch (field) {
      case kceProductFields("productName"): {
        const { productName } = editedProduct;
        if (!(!!productName && productName.replace(/\s/g, "").length > 0)) {
          return false;
        } else {
          return _.values(pdApi.kceProducts).find(
            product => product.id !== editedProduct.id && product.productName.trim() === productName.trim()
          )
            ? false
            : true;
        }
      }
      case kceProductFields("productNumber"): {
        if (!Number.isInteger(editedProduct.productNumber)) return false;
        const { productNumber } = editedProduct;
        return _.values(pdApi.kceProducts).find(
          product => product.id !== editedProduct.id && product.productNumber === productNumber
        )
          ? false
          : true;
      }
      default:
        return true;
    }
  };

  private isAllFieldsValid = (): boolean => {
    const temp = [kceProductFields("productName"), kceProductFields("productNumber")];
    for (const key of temp) {
      if (!this.isFieldValid(key)) {
        return false;
      }
    }
    return true;
  };

}

const mapStateToProps = (state: State.Root) => ({
  editedProduct: state.editView.editedObject,
  storedProduct: state.editView.storedObject,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser,
  ioConfigurations: state.ioConfig.ioConfigurations
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditKCEProduct);


